import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { SearchIcon, XIcon, PlusIcon } from '@/components/icons';
import type { SymbolInfo } from '@/types';
import { trackWatchlistAction, trackSymbolSearch } from '@/services/gtag';
import { prepareSymbols, filterSymbols } from '@/utils/symbolSearch';

type Tab = 'all' | 'stocks' | 'indexes';

interface Props {
  onClose: () => void;
  title?: string;
  /** Single-pick: call callback then close */
  onSelect?: (s: SymbolInfo) => void;
  /** Multi-add: call callback, stay open, show + per row */
  onAdd?: (s: SymbolInfo) => void;
  /** Symbols already in list — show greyed "In list" badge in onAdd mode */
  inList?: string[];
  /** Symbols to hide entirely from results */
  excludeSymbols?: string[];
  /** Override the default z-index (50). Use a higher value to appear above other modals. */
  zIndex?: number;
}

export function SymbolSearchModal({
  onClose,
  title = 'Symbol search',
  onSelect,
  onAdd,
  inList,
  excludeSymbols,
  zIndex,
}: Props) {
  const [query,      setQuery]      = useState('');
  const [tab,        setTab]        = useState<Tab>('all');
  const [allSymbols, setAllSymbols] = useState<SymbolInfo[]>([]);
  const [focused,    setFocused]    = useState(0);
  const inputRef   = useRef<HTMLInputElement>(null);
  const focusedRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const addToWatchlist = useChartStore((s) => s.addToWatchlist);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    api.symbols()
      .then((res) => setAllSymbols(prepareSymbols(res.symbols)))
      .catch(() => {});
  }, []);

  // Scroll focused row into view on keyboard nav
  useEffect(() => { focusedRef.current?.scrollIntoView({ block: 'nearest' }); }, [focused]);

  const q = query.toLowerCase();

  const filtered = filterSymbols(allSymbols, query, {
    type: tab === 'stocks' ? 'stock' : tab === 'indexes' ? 'index' : undefined,
    exclude: excludeSymbols,
  });

  useEffect(() => { setFocused(0); }, [query, tab]);

  function select(s: SymbolInfo) {
    if (onAdd) {
      onAdd(s);
      return; // stay open
    }
    if (onSelect) {
      onSelect(s);
      onClose();
      return;
    }
    addToWatchlist({ symbol: s.symbol, name: s.name, type: s.type, logo: s.logo });
    trackWatchlistAction('add', s.symbol);
    if (q) trackSymbolSearch(q, filtered.length);
    navigate(`/chart/${s.symbol}`);
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocused((f) => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === 'Enter') {
      if (filtered[focused]) select(filtered[focused]);
    }
    // Escape is handled globally by <Modal>.
  }

  const stockCount = allSymbols.filter((s) => s.type === 'stock').length;
  const indexCount = allSymbols.filter((s) => s.type === 'index').length;
  const allCount   = allSymbols.length;

  const TABS: { id: Tab; label: string; count: number }[] = [
    { id: 'all',     label: 'All',     count: allCount   },
    { id: 'stocks',  label: 'Stocks',  count: stockCount },
    { id: 'indexes', label: 'Indexes', count: indexCount },
  ];

  const inListSet = new Set(inList ?? []);

  return (
    <Modal onClose={onClose} size="xl" zIndex={zIndex} className="h-[640px] max-h-[80vh]">
      {/* Drag handle */}
      <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
        <div className="w-10 h-1 rounded-full bg-modal-muted/30" />
      </div>

      {/* Header — no bottom border; grouped with search + filter pills below
          it, which share one divider after the pills instead. */}
      <div className="flex items-center justify-between px-5 pt-2 pb-4 shrink-0">
        <span className="text-lg font-semibold text-modal-text">{title}</span>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
          aria-label="Close"
        >
          <XIcon size={15} />
        </button>
      </div>

      {/* Search bar */}
        <div className="px-4 pb-3 shrink-0">
          <div className="flex items-center gap-2.5 px-3.5 h-11 bg-bg border border-border rounded-xl focus-within:border-accent transition-colors">
            <SearchIcon size={15} className="text-modal-muted shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search symbol or company name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              className="flex-1 bg-transparent text-modal-text text-base outline-none placeholder:text-modal-muted"
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                className="p-0.5 rounded-full text-modal-muted hover:text-modal-text transition-colors"
                aria-label="Clear search"
              >
                <XIcon size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-1.5 px-4 pb-3 shrink-0">
          {TABS.map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                tab === id
                  ? 'bg-accent text-white'
                  : 'bg-modal-hover text-modal-muted hover:text-modal-text'
              }`}
            >
              {label}
              {allSymbols.length > 0 && (
                <span className={`ml-1.5 ${tab === id ? 'text-white/70' : 'text-modal-muted'}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-border shrink-0" />

        {/* Symbol cards — responsive grid, wraps at different screen sizes */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {allSymbols.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-modal-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-1.5">
              <span className="text-sm text-modal-text">No results for "{query}"</span>
              <span className="text-xs text-modal-muted">Try a different symbol or company name</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 p-4">
              {filtered.map((s, i) => {
                const already = inListSet.has(s.symbol);
                const isFocused = i === focused;
                return (
                  <button
                    key={s.symbol}
                    ref={isFocused ? focusedRef : undefined}
                    onClick={() => { if (!already || !onAdd) select(s); }}
                    onMouseEnter={() => setFocused(i)}
                    disabled={already && !!onAdd}
                    className={`flex flex-col gap-1.5 p-3 rounded-xl border text-left transition-all ${
                      already && onAdd
                        ? 'opacity-50 cursor-default border-border bg-panel'
                        : isFocused
                          ? 'border-accent bg-modal-hover ring-1 ring-accent'
                          : 'border-border bg-panel hover:bg-modal-hover hover:border-accent/50'
                    }`}
                  >
                    {/* Top row: logo + symbol + badge */}
                    <div className="flex items-center gap-2.5 min-w-0">
                      <SymbolLogo s={s} />
                      <span className="font-bold text-sm text-up truncate">{s.symbol}</span>
                      <div className="ml-auto shrink-0">
                        {onAdd ? (
                          already
                            ? <span className="text-[11px] text-accent">In list</span>
                            : <PlusIcon size={14} className="text-modal-muted" />
                        ) : (
                          <span className="text-[10px] text-modal-muted px-1.5 py-0.5 rounded border border-border">
                            {s.type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Name */}
                    <span className="text-xs text-modal-muted truncate">{s.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

      {/* Footer */}
      <div className="h-px bg-border shrink-0" />
      <div className="px-5 py-3 text-center">
        <span className="text-xs text-modal-muted">Search by symbol or company name</span>
      </div>
    </Modal>
  );
}

/* ── Logo / type-icon fallback ───────────────────────────────────────────── */

function SymbolLogo({ s }: { s: SymbolInfo }) {
  const [err, setErr] = useState(false);
  if (s.logo && !err) {
    return (
      <img
        src={s.logo}
        alt=""
        onError={() => setErr(true)}
        className="w-8 h-8 rounded object-contain bg-modal-hover shrink-0"
      />
    );
  }
  return (
    <div
      className={`w-9 h-9 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
        s.type === 'stock'
          ? 'bg-up/15 text-up'
          : 'bg-purple-500/15 text-purple-400'
      }`}
    >
      {s.type === 'stock' ? 'S' : 'I'}
    </div>
  );
}
